import React from 'react';
import { QuizRounded } from '@mui/icons-material';

interface CuriosityScoreIndicatorProps {
  score: number;
  tip?: string; // no longer shown; kept so callers don't break
  showQuiz?: boolean;
  quizLabel?: string;
  onQuizClick?: () => void;
  quizOpen?: boolean; // when the side quiz panel is open, shift left to clear it on large screens
  // The score is HIDDEN from students by default. It measures how much of the
  // topic they have understood, and a number attached to that — sitting on
  // screen while they are mid-thought — invites them to play the number instead
  // of following the idea. Teachers still see it in TeacherConversationView.
  // The quiz button lives in this same component, which is why this is a flag
  // rather than the caller dropping the component.
  showScore?: boolean;
}

// Score → level ladder (per-chat). Highest matching threshold wins.
//
// The score means how much of the TOPIC has landed — core elements understood
// over core elements in the topic — not how chatty or enthusiastic the student
// has been. So the ladder describes progress THROUGH the topic. It deliberately
// stops short of naming the child ("Genius", "Mastermind"): the number can now
// fall as easily as it rises, and a label that reads as a verdict on the child
// rather than on the topic would sting on the way down.
const LEVELS = [
  { min: 100, name: 'Topic complete', emoji: '🏆' },
  { min: 80, name: 'Knows it', emoji: '💡' },
  { min: 55, name: 'Almost there', emoji: '⚡' },
  { min: 25, name: 'Getting there', emoji: '⭐' },
  { min: 0, name: 'Just started', emoji: '🧭' },
];

const CuriosityScoreIndicator: React.FC<CuriosityScoreIndicatorProps> = ({
  score,
  showQuiz = false,
  quizLabel = 'Take quiz',
  onQuizClick,
  quizOpen = false,
  showScore = false,
}) => {
  const safeScore = Number.isFinite(score) ? Math.max(0, Math.min(100, Math.round(score))) : 0;
  const level = LEVELS.find((l) => safeScore >= l.min) || LEVELS[LEVELS.length - 1];

  // Nothing to float over the chat — don't leave an empty pill container behind.
  if (!showScore && !(showQuiz && onQuizClick)) return null;

  return (
    <div
      /* right-20 clears the floating action button, which is fixed at the same
         bottom-28 with right-6 and z-[70] and would otherwise sit on top of
         whatever is rightmost here. The score pill used to occupy that space
         and hide the collision; with the pill off, the quiz button slides
         right and lands underneath the FAB. */
      className={`fixed bottom-28 right-20 z-40 flex items-center gap-2 ${
        quizOpen ? 'lg:right-[27rem] xl:right-[30rem]' : ''
      }`}
    >
      {showQuiz && onQuizClick && (
        <button
          type="button"
          onClick={onQuizClick}
          className="flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 sm:text-sm"
        >
          <QuizRounded sx={{ fontSize: 16 }} />
          <span className="max-w-[9rem] truncate sm:max-w-none">{quizLabel}</span>
        </button>
      )}
      {showScore && (
        <div className="flex items-center gap-2 rounded-full border border-violet-200 bg-white/95 px-4 py-1.5 shadow-sm">
          <span className="text-lg leading-none">{level.emoji}</span>
          <span className="text-sm font-semibold text-slate-800">{level.name}</span>
          <span className="ml-0.5 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700">
            {safeScore}
          </span>
        </div>
      )}
    </div>
  );
};

export default CuriosityScoreIndicator;
